// m8-media-session-bridge.js — Media Session API bridge for OS-level media integration.
// Exposes window.M8 = { init, updateMetadata, isIOS, isMobile,
//                       onStationChange, onTrackUpdate, onMuteChanged }.
//
// Manages navigator.mediaSession metadata (lock screen, watch, notification center)
// and action handlers (Bluetooth controls, lock screen buttons).
// Delegates to M1 for toggleMute, M3 for next/previous station, and M6 for track metadata.

window.M8 = (() => {
  let _lastStationName = null;
  let _lastTitle = null;
  let _lastArtist = null;
  let _lastRaw = null;
  let _activated = false;
  let _visHandler = null;
  let _focusHandler = null;
  let _lastPokeTime = 0;
  let _pokeTimer = null;

  // ---------------------------------------------------------------------------
  // Platform detection
  // ---------------------------------------------------------------------------

  /** Returns true on iOS Safari (where audio.volume is read-only). */
  function isIOS() {
    if (window.M1 && typeof M1.isIOS === 'function') return M1.isIOS();
    const ua = navigator.userAgent;
    return /iPhone|iPod/.test(ua);
  }

  /** Returns true on mobile browsers in general. */
  function isMobile() {
    return /Mobi|Android|iPhone|iPad|iPod/.test(navigator.userAgent);
  }

  // ---------------------------------------------------------------------------
  // playbackState helpers
  // ---------------------------------------------------------------------------

  function _setPlaying() {
    if (navigator.mediaSession && navigator.mediaSession.playbackState !== 'playing') {
      navigator.mediaSession.playbackState = 'playing';
    }
  }

  function _setPaused() {
    if (navigator.mediaSession && navigator.mediaSession.playbackState !== 'paused') {
      navigator.mediaSession.playbackState = 'paused';
    }
  }

  /**
   * Briefly flips playbackState to force iOS to refresh the lock screen
   * Now Playing display. iOS ignores MediaMetadata assignments made while
   * the screen is locked unless playbackState changes. The state flip is
   * asynchronous (setTimeout 50ms) — iOS needs at least one event-loop tick
   * to process the intermediate state and trigger a lock screen re-render.
   * A synchronous flip is coalesced away; the pause button works because
   * its state transition is inherently asynchronous (user must press play).
   * Rate-limited to once every 3 seconds.
   */
  function _pokePlaybackState() {
    if (!navigator.mediaSession) return;
    var now = Date.now();
    if (now - _lastPokeTime < 3000) return;
    _lastPokeTime = now;
    var current = navigator.mediaSession.playbackState;
    if (current !== 'playing' && current !== 'paused') return;
    navigator.mediaSession.playbackState = current === 'playing' ? 'paused' : 'playing';
    setTimeout(function () {
      if (navigator.mediaSession) {
        navigator.mediaSession.playbackState = current;
      }
    }, 50);
  }

  // ---------------------------------------------------------------------------
  // iOS Media Session activation — required for lock screen controls
  // ---------------------------------------------------------------------------

  /**
   * On iOS Safari, the Media Session stays dormant until a pause→play cycle
   * occurs within a user gesture context, AND action handlers must be
   * registered within that same gesture. This one-time activation hooks into
   * the first user interaction and configures everything iOS needs.
   */
  function _registerActions() {
    if (!navigator.mediaSession) return;
    navigator.mediaSession.setActionHandler('previoustrack', function () {
      if (window.M3 && M3.previous) M3.previous();
    });
    navigator.mediaSession.setActionHandler('nexttrack', function () {
      if (window.M3 && M3.next) M3.next();
    });
    navigator.mediaSession.setActionHandler('pause', function () {
      if (window.M1 && M1.toggleMute) M1.toggleMute();
      _setPaused();
    });
    navigator.mediaSession.setActionHandler('play', function () {
      if (window.M1 && M1._muted && M1.toggleMute) M1.toggleMute();
      _setPlaying();
    });
    // Explicitly null seek handlers — iOS shows 10s rewind/forward if these exist
    navigator.mediaSession.setActionHandler('seekforward', null);
    navigator.mediaSession.setActionHandler('seekbackward', null);
    navigator.mediaSession.setActionHandler('seekto', null);
    navigator.mediaSession.setActionHandler('stop', null);
  }

  function _scheduleActivation() {
    if (_activated) return;
    if (!isIOS()) return;

    var events = ['click', 'touchend', 'keydown'];
    function activate() {
      if (_activated) return;

      Logger.log({ ts: Date.now(), module: 'M8', fn: 'activate_ios', inputs: {}, output: 'user gesture captured', durationMs: 0, error: null });

      try {
        // Step 1: Re-register ALL action handlers within the user gesture.
        // iOS ignores handlers set outside a user gesture context.
        _registerActions();

        // Step 2: Synchronous pause→play cycle within the same gesture.
        // iOS requires this to associate the action handlers with the
        // audio session. If M1 has no audio element yet (deferred playback),
        // use a temporary silent element to satisfy the requirement.
        var woken = false;
        if (window.M1 && M1.wakeMediaSession) {
          var result = M1.wakeMediaSession();
          woken = result && result.success;
        }
        if (!woken) {
          // Fallback: create a temporary silent Audio element within the
          // gesture to satisfy iOS's pause→play activation requirement.
          // Without this, iOS ignores action handlers and defaults to
          // seek buttons (10s rewind/forward) instead of prev/next track.
          try {
            var tmp = new Audio();
            tmp.volume = 0.001;
            var tmpPlay = tmp.play();
            if (tmpPlay && typeof tmpPlay.then === 'function') {
              tmpPlay.then(function () { tmp.pause(); }).catch(function () {});
            } else {
              tmp.pause();
            }
          } catch (e2) {
            // Best-effort — handlers are already registered, which is the
            // critical part. The temporary audio is a bonus.
          }
        }

        _activated = true;
        events.forEach(function (e) { document.removeEventListener(e, activate, true); });
        Logger.log({ ts: Date.now(), module: 'M8', fn: 'activate_ios', inputs: {}, output: 'activation complete', durationMs: 0, error: null });
      } catch (e) {
        Logger.log({ ts: Date.now(), module: 'M8', fn: 'activate_ios_error', inputs: {}, output: null, durationMs: 0, error: e });
      }
    }

    events.forEach(function (e) {
      document.addEventListener(e, activate, { once: false, capture: true });
    });

    Logger.log({ ts: Date.now(), module: 'M8', fn: '_scheduleActivation', inputs: { ios: true }, output: 'listening for first user gesture', durationMs: 0, error: null });
  }

  // ---------------------------------------------------------------------------
  // Media Session metadata
  // ---------------------------------------------------------------------------

  /**
   * Sets navigator.mediaSession.metadata with station + track info.
   * @param {string|null} stationName — station broadcast name or null
   * @param {{ artist?: string, title?: string }|null} trackInfo — current track
   */
  function updateMetadata(stationName, trackInfo) {
    if (!navigator.mediaSession) {
      Logger.log({ ts: Date.now(), module: 'M8', fn: 'updateMetadata', inputs: { stationName, trackInfo }, output: null, durationMs: 0, error: 'mediaSession not available' });
      return false;
    }

    // Cache station name for future track-only updates
    if (stationName != null) _lastStationName = stationName;

    // Resolve title
    var title;
    if (trackInfo && trackInfo.artist && trackInfo.title) {
      title = trackInfo.artist + ' \u2013 ' + trackInfo.title; // en-dash
    } else if (trackInfo && trackInfo.title) {
      title = trackInfo.title;
    } else if (trackInfo && trackInfo.artist) {
      title = trackInfo.artist;
    } else {
      title = _lastStationName || 'rad1o';
    }

    // Resolve artist
    var artist = _lastStationName || '';

    // Skip update if nothing changed — avoids unnecessary lock-screen re-renders
    // that cause jank on iOS (each MediaMetadata assignment triggers a UI refresh).
    // Also skip if raw metadata hasn't changed (handles stations with no artist/title
    // fields, where computed title always falls back to station name).
    var raw = trackInfo && trackInfo.raw;
    if (title === _lastTitle && artist === _lastArtist && raw === _lastRaw) {
      return true;
    }
    _lastTitle = title;
    _lastArtist = artist;
    if (raw !== undefined) _lastRaw = raw;

    try {
      // iOS requires square artwork at specific sizes. Providing a full range
      // ensures it works across lock screen, Control Center, and fullscreen player.
      navigator.mediaSession.metadata = new MediaMetadata({
        title: title,
        artist: artist,
        artwork: [
          { src: '/assets/icon-96.png',  sizes: '96x96',   type: 'image/png' },
          { src: '/assets/icon-128.png', sizes: '128x128', type: 'image/png' },
          { src: '/assets/icon-192.png', sizes: '192x192', type: 'image/png' },
          { src: '/assets/icon-256.png', sizes: '256x256', type: 'image/png' },
          { src: '/assets/icon-384.png', sizes: '384x384', type: 'image/png' },
          { src: '/assets/icon-512.png', sizes: '512x512', type: 'image/png' },
        ],
      });
    } catch (e) {
      Logger.log({ ts: Date.now(), module: 'M8', fn: 'updateMetadata', inputs: { stationName, trackInfo }, output: null, durationMs: 0, error: e });
      return false;
    }

    Logger.log({ ts: Date.now(), module: 'M8', fn: 'updateMetadata', inputs: { stationName, trackInfo }, output: { title: title, artist: artist }, durationMs: 0, error: null });
    // Defer the playbackState poke by 2 seconds. This gives Safari's bridge
    // time to push the new MediaMetadata to MPNowPlayingInfoCenter before
    // we force a lock screen re-render, AND gives the backend cache time to
    // refresh if M6 polled immediately after a track change.
    // Cancel any pending poke so we don't pile up timers during rapid updates.
    if (_pokeTimer) { clearTimeout(_pokeTimer); _pokeTimer = null; }
    _pokeTimer = setTimeout(function () {
      _pokeTimer = null;
      _pokePlaybackState();
    }, 2000);
    return true;
  }

  // ---------------------------------------------------------------------------
  // Init — wire media session action handlers
  // ---------------------------------------------------------------------------

  function _onVisibilityOrFocus() {
    // Re-assert playbackState and action handlers when the tab regains
    // visibility or focus. Some platforms (especially desktop browsers)
    // reset or ignore Media Session action handlers when the page's focus
    // context changes (e.g. switching browser tabs). Without this,
    // lock screen and media key prev/next stop working on non-active tabs.
    _setPlaying();
    _registerActions();
    // Trigger an immediate metadata poll on M6. iOS throttles background
    // timers, so the regular 15s interval may not have fired while the
    // screen was locked. This gets fresh track data as fast as possible
    // after the user unlocks, so the next lock screen refresh is current.
    if (window.M6 && M6.forcePoll) M6.forcePoll();
  }

  function init() {
    if (!navigator.mediaSession) {
      Logger.log({ ts: Date.now(), module: 'M8', fn: 'init', inputs: {}, output: null, durationMs: 0, error: 'mediaSession not available' });
      return false;
    }

    // Re-assert Media Session state on visibility/focus changes.
    // Prevents prev/next from breaking after tab switches.
    if (typeof document !== 'undefined') {
      _visHandler = function () {
        if (!document.hidden) _onVisibilityOrFocus();
      };
      document.addEventListener('visibilitychange', _visHandler);
    }
    if (typeof window !== 'undefined') {
      _focusHandler = function () { _onVisibilityOrFocus(); };
      window.addEventListener('focus', _focusHandler);
    }

    // Register ALL action handlers BEFORE any audio starts playing.
    try {
      navigator.mediaSession.setActionHandler('previoustrack', function () {
        Logger.log({ ts: Date.now(), module: 'M8', fn: 'previoustrack', inputs: {}, output: null, durationMs: 0, error: null });
        if (window.M3 && M3.previous) M3.previous();
      });

      navigator.mediaSession.setActionHandler('nexttrack', function () {
        Logger.log({ ts: Date.now(), module: 'M8', fn: 'nexttrack', inputs: {}, output: null, durationMs: 0, error: null });
        if (window.M3 && M3.next) M3.next();
      });

      navigator.mediaSession.setActionHandler('pause', function () {
        Logger.log({ ts: Date.now(), module: 'M8', fn: 'pause', inputs: { isIOS: isIOS() }, output: null, durationMs: 0, error: null });
        if (window.M1 && M1.toggleMute) M1.toggleMute();
        _setPaused();
      });

      navigator.mediaSession.setActionHandler('play', function () {
        Logger.log({ ts: Date.now(), module: 'M8', fn: 'play', inputs: { isIOS: isIOS() }, output: null, durationMs: 0, error: null });
        // Only unmute — never mute on play
        if (window.M1 && M1._muted && M1.toggleMute) {
          M1.toggleMute();
        }
        _setPlaying();
      });

      // Explicitly NULL seek handlers — on iOS, setting these hides prev/next
      // buttons and shows 10s rewind/forward instead.
      navigator.mediaSession.setActionHandler('seekforward', null);
      navigator.mediaSession.setActionHandler('seekbackward', null);
      navigator.mediaSession.setActionHandler('seekto', null);
      navigator.mediaSession.setActionHandler('stop', null);
    } catch (e) {
      Logger.log({ ts: Date.now(), module: 'M8', fn: 'init_setActionHandler', inputs: {}, output: null, durationMs: 0, error: e });
    }

    // Schedule iOS Media Session activation on first user gesture
    _scheduleActivation();

    Logger.log({ ts: Date.now(), module: 'M8', fn: 'init', inputs: { isIOS: isIOS(), isMobile: isMobile() }, output: 'M8 initialised', durationMs: 0, error: null });
    return true;
  }

  // ---------------------------------------------------------------------------
  // Metadata hooks (called by M5)
  // ---------------------------------------------------------------------------

  /** Called by M5 when station navigates. Updates media session metadata.
   *  Also attempts iOS activation if still pending — station navigation
   *  implies audio is about to play within the current user gesture. */
  function onStationChange(index) {
    try {
      var stations = window.M2 && M2.getStations ? M2.getStations() : [];
      var st = stations.find(function (s) { return s && s.index === index; });
      var name = st ? st.label : null;
      updateMetadata(name, null);
      _setPlaying();
      // If activation hasn't happened yet (e.g. incognito mode where audio
      // wasn't available at init time), try now — we're inside the user gesture
      // that triggered playback.
      _tryActivate();
    } catch (e) {
      Logger.log({ ts: Date.now(), module: 'M8', fn: 'onStationChange', inputs: { index: index }, output: null, durationMs: 0, error: e });
    }
  }

  /** Called by M5 when track metadata changes. Updates media session metadata. */
  function onTrackUpdate(data) {
    try {
      updateMetadata(null, { artist: data.artist, title: data.title, raw: data.raw });
    } catch (e) {
      Logger.log({ ts: Date.now(), module: 'M8', fn: 'onTrackUpdate', inputs: data, output: null, durationMs: 0, error: e });
    }
  }

  function onMuteChanged(state) {
    if (state.muted) _setPaused();
    else _setPlaying();
  }

  /**
   * Re-registers Media Session action handlers and performs the iOS
   * activation pause→play cycle. Safe to call at any time (e.g. after
   * station switch where iOS may reset the Media Session).
   */
  function rearm() {
    _registerActions();
    if (isIOS() && window.M1 && M1.wakeMediaSession) {
      M1.wakeMediaSession();
    }
  }

  /** Internal: attempt activation if pending and audio is available. */
  function _tryActivate() {
    if (_activated) return;
    if (!isIOS()) return;
    if (!window.M1 || !M1.wakeMediaSession) return;
    var result = M1.wakeMediaSession();
    if (result && result.success) {
      _activated = true;
      Logger.log({ ts: Date.now(), module: 'M8', fn: '_tryActivate', inputs: {}, output: 'activation complete (via onStationChange)', durationMs: 0, error: null });
    }
  }

  return { init: init, updateMetadata: updateMetadata, isIOS: isIOS, isMobile: isMobile, onStationChange: onStationChange, onTrackUpdate: onTrackUpdate, onMuteChanged: onMuteChanged, tryActivate: _tryActivate, rearm: rearm };
})();
